import { useEffect, useState, useContext, useMemo, useRef } from "react";
import { ChevronLeftIcon } from "@heroicons/react/24/outline";
import { callFunction } from "../api/callFunction";
import { GlobalContext } from "../context/GlobalContext";
import { formatRelativeTime, formatTimestamp } from "../components/MessageThread";

// A single turn in a saved "Ask Gist AI" chat. Unlike SMS messages (typed/directional),
// Gist AI turns carry a role plus the rich content the member saw - event cards,
// people grids, a "start hosting" CTA, and a report receipt when Gist AI filed
// something on their behalf. We surface those as compact context under the bubble.
function GistAIBubble({ msg }) {
  const isMember = msg.role === "user";
  const cards = Array.isArray(msg.eventCards) ? msg.eventCards : [];
  const groups = Array.isArray(msg.peopleGroups) ? msg.peopleGroups : [];
  const receipt = msg.reportReceipt;

  return (
    <div className={`flex ${isMember ? "justify-start" : "justify-end"}`}>
      <div className={`flex flex-col ${isMember ? "items-start" : "items-end"} max-w-[78%]`}>
        <span className="text-[10px] font-semibold text-gray-400 dark:text-gray-500 mb-0.5 px-1">
          {isMember ? "Member" : "Gist AI"}
        </span>
        <div
          className={`px-3 py-2 rounded-2xl text-sm whitespace-pre-wrap break-words ${
            isMember
              ? "bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-800 dark:text-gray-200 rounded-bl-sm"
              : "bg-primary text-white rounded-br-sm"
          }`}
        >
          {msg.text || <span className="opacity-60">(no text)</span>}
        </div>

        {/* Rich content Gist AI attached to this reply. */}
        {cards.length > 0 && (
          <div className={`mt-1 flex flex-wrap gap-1 ${isMember ? "" : "justify-end"}`}>
            {cards.map((c, i) => (
              <span
                key={i}
                className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300"
              >
                📅 {c.title || "event"}
                {c.dateLabel ? ` · ${c.dateLabel}` : ""}
              </span>
            ))}
          </div>
        )}
        {groups.length > 0 && (
          <div className={`mt-1 flex flex-wrap gap-1 ${isMember ? "" : "justify-end"}`}>
            {groups.map((g, i) => (
              <span
                key={i}
                className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300"
              >
                👥 {g.eventTitle || "people"} ({(g.people || []).length})
              </span>
            ))}
          </div>
        )}
        {msg.action === "startHosting" && (
          <span className="mt-1 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
            ⚡ {msg.actionTitle || "Start a new event"}
          </span>
        )}
        {receipt && receipt.category && (
          <span className="mt-1 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300">
            📨 report filed · {receipt.category}
            {receipt.reference ? ` (${receipt.reference})` : ""}
          </span>
        )}
      </div>
    </div>
  );
}

export default function GistAIConversations() {
  const { globalState } = useContext(GlobalContext);
  const isSuperAdmin = globalState.isSuperAdmin;

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [selectedId, setSelectedId] = useState(null);

  const [searchQuery, setSearchQuery] = useState("");

  // Selected thread (fetched on demand via the super-admin callable).
  const [thread, setThread] = useState(null);
  const [threadLoading, setThreadLoading] = useState(false);
  const [threadError, setThreadError] = useState(null);
  const threadContainerRef = useRef(null);

  // --- Build the inbox via the super-admin callable. The read over all members'
  // owner-only gistAiConversations happens server-side with the Admin SDK. ---
  useEffect(() => {
    if (!isSuperAdmin) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const data = await callFunction("adminListGistAiConversations", undefined, { timeout: 120000 });
        const convos = (data?.conversations || []).sort(
          (a, b) => b.updatedMs - a.updatedMs
        );
        if (!cancelled) {
          setConversations(convos);
          setLoading(false);
        }
      } catch (err) {
        console.error("Error loading Gist AI conversations:", err);
        if (!cancelled) {
          setLoadError(err?.message || "Failed to load conversations.");
          setLoading(false);
        }
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [isSuperAdmin]);

  // --- Fetch the selected chat's full message array on selection. ---
  useEffect(() => {
    if (!selectedId) {
      setThread(null);
      return;
    }
    let cancelled = false;
    setThreadLoading(true);
    setThreadError(null);
    callFunction("adminGetGistAiConversation", { id: selectedId }, { timeout: 120000 })
      .then((data) => {
        if (cancelled) return;
        setThread(data || null);
        setThreadLoading(false);
        setTimeout(() => {
          if (threadContainerRef.current) {
            threadContainerRef.current.scrollTop =
              threadContainerRef.current.scrollHeight;
          }
        }, 50);
      })
      .catch((err) => {
        console.error("Error loading Gist AI thread:", err);
        if (cancelled) return;
        setThreadError(err?.message || "Failed to load conversation.");
        setThreadLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedId]);

  const displayName = (convo) =>
    convo.name || convo.phone || `User ${(convo.uid || "").slice(0, 6)}`;

  const filteredConversations = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return conversations;
    return conversations.filter(
      (c) =>
        (c.name && c.name.toLowerCase().includes(q)) ||
        (c.phone && c.phone.toLowerCase().includes(q)) ||
        (c.uid && c.uid.toLowerCase().includes(q)) ||
        (c.title && c.title.toLowerCase().includes(q)) ||
        (c.preview && c.preview.toLowerCase().includes(q))
    );
  }, [conversations, searchQuery]);

  const selectedConvo = useMemo(
    () => conversations.find((c) => c.id === selectedId) || null,
    [conversations, selectedId]
  );

  if (!isSuperAdmin) {
    return (
      <div className="px-4 sm:px-6 lg:px-8 mt-6">
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">Gist AI</h1>
        <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
          This view is only available to super admins.
        </p>
      </div>
    );
  }

  const messages = Array.isArray(thread?.messages) ? thread.messages : [];

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="mt-4 sm:mt-0">
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">Gist AI</h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Every member's saved chat with the Gist AI concierge, most recent first.
          {conversations.length > 0 && (
            <span> Showing {conversations.length} chats.</span>
          )}
        </p>
      </div>

      <div className="mt-4 flex flex-col lg:flex-row gap-4 h-[calc(100vh-180px)]">
        {/* Inbox list — full-screen on mobile, hidden once a chat is open. */}
        <div
          className={`${
            selectedId ? "hidden lg:flex" : "flex"
          } lg:w-80 flex-shrink-0 flex-col border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 overflow-hidden`}
        >
          <div className="p-3 border-b border-gray-200 dark:border-gray-700">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search name, phone, title, or message…"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>

          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
              </div>
            ) : loadError ? (
              <div className="p-4 text-sm text-red-600 dark:text-red-400">{loadError}</div>
            ) : filteredConversations.length === 0 ? (
              <div className="p-4 text-sm text-gray-400 dark:text-gray-500 text-center">
                No conversations found.
              </div>
            ) : (
              filteredConversations.map((convo) => {
                const isSelected = convo.id === selectedId;
                return (
                  <button
                    key={convo.id}
                    onClick={() => setSelectedId(convo.id)}
                    className={`w-full text-left px-3 py-3 border-b border-gray-100 dark:border-gray-800 transition-colors ${
                      isSelected ? "bg-primary/10" : "hover:bg-gray-50 dark:hover:bg-gray-700"
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium text-gray-900 dark:text-gray-100 truncate">
                        {displayName(convo)}
                      </span>
                      <span className="text-xs text-gray-400 dark:text-gray-500 flex-shrink-0">
                        {formatRelativeTime(convo.updatedMs)}
                      </span>
                    </div>
                    <div className="text-xs text-gray-600 dark:text-gray-300 truncate mt-0.5">
                      {convo.title || "New chat"}
                    </div>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300 flex-shrink-0">
                        {convo.messageCount} msg
                      </span>
                      <span className="text-xs text-gray-500 dark:text-gray-400 truncate">
                        {convo.preview || "(no text)"}
                      </span>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>

        {/* Thread pane — hidden on mobile until a chat is picked. */}
        <div
          className={`${
            selectedId ? "flex" : "hidden lg:flex"
          } flex-1 flex-col border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 overflow-hidden min-h-0`}
        >
          {!selectedConvo ? (
            <div className="flex-1 flex items-center justify-center text-gray-400 dark:text-gray-500 text-sm">
              Select a conversation to view it.
            </div>
          ) : (
            <>
              <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center gap-2">
                <button
                  onClick={() => setSelectedId(null)}
                  aria-label="Back to conversations"
                  className="lg:hidden -ml-2 p-1.5 rounded-md text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  <ChevronLeftIcon className="h-5 w-5" />
                </button>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-gray-900 dark:text-gray-100 truncate">
                    {displayName(selectedConvo)}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                    {selectedConvo.title || "New chat"}
                    {selectedConvo.updatedMs
                      ? ` · ${formatTimestamp(selectedConvo.updatedMs)}`
                      : ""}
                  </div>
                </div>
              </div>

              <div
                ref={threadContainerRef}
                className="flex-1 overflow-y-auto p-4 bg-gray-50 dark:bg-gray-800 space-y-3"
              >
                {threadLoading ? (
                  <div className="flex justify-center py-8">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
                  </div>
                ) : threadError ? (
                  <div className="text-center text-red-600 dark:text-red-400 text-sm">
                    {threadError}
                  </div>
                ) : messages.length === 0 ? (
                  <div className="text-center text-gray-400 dark:text-gray-500 text-sm">
                    No messages.
                  </div>
                ) : (
                  messages.map((msg, i) => <GistAIBubble key={i} msg={msg} />)
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
